import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@/firebase';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import LoadingSpinner from '@/shared/components/LoadingSpinner';

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUserData } = useEnhancedAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let isVerifying = false; // Prevent multiple calls

    const verifyToken = async () => {
      if (isVerifying) return; // Prevent duplicate calls
      isVerifying = true;

      const urlParams = new URLSearchParams(location.search);
      const token = urlParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link.');
        return;
      }

      try {
        const verifyEmailToken = httpsCallable<{ token: string }, { success: boolean; alreadyVerified?: boolean }>(
          functions,
          'verifyEmailToken'
        );
        const result = await verifyEmailToken({ token });

        if (result.data.success) {
          setStatus('success');

          if (result.data.alreadyVerified) {
            setMessage('Your email was already verified! You\'re all set to receive updates.');
          } else {
            setMessage('Email verified successfully! You can now receive important updates.');

            if (typeof gtag !== 'undefined') {
              gtag('event', 'email_verified', { method: 'custom_link' });
            }
          }

          // Reload auth user in the background (don't await to avoid loading flash)
          if (auth.currentUser) {
            auth.currentUser.reload().then(() => {
              logger.info('VerifyEmail', 'Firebase Auth user reloaded', { emailVerified: auth.currentUser?.emailVerified });
            });
          }

          // Brief moment to show success, then navigate
          await new Promise(resolve => setTimeout(resolve, 800));

          const urlParams = new URLSearchParams(location.search);
          const redirect = urlParams.get('redirect') || '/dashboard';
          navigate(`/account-setup?redirect=${encodeURIComponent(redirect)}`, { replace: true });
        }
      } catch (error) {
        logger.error('VerifyEmail', 'Verification failed', { error });

        const errorCode = (error as { code?: string }).code;

        if (errorCode === 'functions/deadline-exceeded') {
          setStatus('error');
          setMessage('This verification link has expired. Please request a new verification email.');
        } else if (errorCode === 'functions/not-found') {
          setStatus('error');
          setMessage('This verification link is invalid or has already been used.');
        } else {
          setStatus('error');
          setMessage('Email verification failed. Please try again or contact support.');
        }
      }
    };

    verifyToken();
  }, []); // Remove dependencies to prevent re-runs

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-100 rounded-lg shadow-ledge-lg p-6">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <LoadingSpinner />
              <h2 className="mt-4 text-xl font-semibold text-neutral-900">
                Verifying Your Email
              </h2>
              <p className="mt-2 text-neutral-600">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-green-600">
                Success!
              </h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              <div className="mt-4 p-4 bg-primary-100 rounded-lg">
                <p className="text-sm text-primary-700">
                  You'll now receive study reminders and progress updates to help you succeed on your LCSW exam.
                </p>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="mt-4 text-xl font-semibold text-red-600">
                Verification Failed
              </h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              <div className="mt-6 space-y-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => navigate('/settings')}
                  className="w-full px-4 py-2 bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300"
                >
                  Account Settings
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;